import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './TemperatureDisplayDialog.scss';
import { Modal } from 'react-bootstrap';
import Loading from '../LoadingPage/Loading';
import useSensorManagement from '../../Hooks/useSensor';
import moment from 'moment';

interface TemperatureDisplayDialogProps {
    sensorInfo: Record<string, any> | null;
    isOpen: boolean;
    handleClose: () => void;
}

const TemperatureDisplayDialog: React.FC<TemperatureDisplayDialogProps> = ({ sensorInfo, handleClose, isOpen }) => {
    const [temperatureData, setTemperatureData] = useState<any>(null);
    const [temperatureErr, setTemperatureErr] = useState<string | null>(null);
    const [tempSensorLog, setTempSensorLog] = useState<any>(null);
    const [sensorLogDate, setSensorLogDate] = useState<string>(moment().format('MM-DD-YYYY'));
    const { getRetrieveFromZebraAPI, getRetrieveFromZebraLoading, getSensorsLogAPI, getSensorsLogLoading } = useSensorManagement();

    useEffect(() => {
        if (sensorInfo?.id && isOpen) {
            setSensorLogDate(moment().format('MM-DD-YYYY'));
            fetchSensorTemperatureData();
        };
    }, [sensorInfo, isOpen]);

    useEffect(() => {
        if (sensorInfo?.id && isOpen) {
            fetchSensorLog();
        }
    }, [sensorInfo, isOpen, sensorLogDate]);

    const fetchSensorTemperatureData = useCallback(async () => {
        setTemperatureErr(null);
        try {
            const res = await getRetrieveFromZebraAPI({ id: sensorInfo?.id }, true);
            if (res?.result?.sensors?.length) {
                setTemperatureData(res.result.sensors?.[0] || null);
                return;
            }
            setTemperatureErr(res?.message || 'No temperature data available.');
        } catch (error: any) {
            setTemperatureErr(error?.message || 'Failed to fetch temperature data.');
        }
    }, [sensorInfo]);

    const fetchSensorLog = useCallback(async () => {
        try {
            const res = await getSensorsLogAPI({
                RefrigeratorName: sensorInfo?.sensorRefrigerator?.refrigeratorName,
                clientCode: sensorInfo?.clientCode,
                startDate: sensorLogDate,
                endDate: sensorLogDate
            });
            setTempSensorLog(res.length ? res?.[0] : null);
        } catch (error) {
            console.error("Error while getting sensor logs:", error);
        }
    }, [sensorInfo, sensorLogDate]);

    const isToday = moment(sensorLogDate, 'MM-DD-YYYY').isSame(moment(), 'day');

    const goToPrevDay = () => {
        setSensorLogDate(moment(sensorLogDate, 'MM-DD-YYYY').subtract(1, 'day').format('MM-DD-YYYY'));
    };
    const goToNextDay = () => {
        if (!isToday) setSensorLogDate(moment(sensorLogDate, 'MM-DD-YYYY').add(1, 'day').format('MM-DD-YYYY'));
    };

    const parseTemperature = (tempString: string) => {
        if (!tempString) return { celsius: '--', fahrenheit: '--' };
        // Parse "9.53 °C | 49.15 °F" format
        const parts = tempString.split('|').map(part => part.trim());
        const celsius = parts[0]?.replace('°C', '').trim() || '--';
        const fahrenheit = parts[1]?.replace('°F', '').trim() || '--';
        return { celsius, fahrenheit };
    }

    const getStatusDotClass = (color = '') => {
        if (/green/i.test(color)) return 'green';
        if (/red/i.test(color)) return 'red';
        if (/yellow|orange/i.test(color)) return 'orange';
        return 'green';
    }

    const temp = useMemo(() => parseTemperature(temperatureData?.lastUpdatedTemperature), [temperatureData]);
    const sensorStatusDot = useMemo(() => getStatusDotClass(temperatureData?.sensorStatusColor), [temperatureData]);
    const taskStatusDot = useMemo(() => getStatusDotClass(temperatureData?.sensorTaskStatusColor), [temperatureData]);
    const tempNumberColor = sensorStatusDot.includes('red') || taskStatusDot.includes('red') ? 'text-red' : '';

    const dialogTitle = sensorInfo?.sensorRefrigerator?.refrigeratorName || temperatureData?.serialNumber || 'Temperature Monitor';

    const batteryLevel = parseInt(temperatureData?.batteryLevel) || 0;
    const hasBattery = temperatureData?.batteryLevel != null;
    const batteryColorClass = batteryLevel > 50 ? 'full' : batteryLevel > 20 ? 'medium' : 'low';

    return (
        <Modal show={!!sensorInfo?.id && isOpen} centered onHide={handleClose} dialogClassName="temperature-display-modal">
            {(getRetrieveFromZebraLoading || getSensorsLogLoading) && <Loading />}
            <div className="phone-device">
                {/* App Bar */}
                <div className="phone-app-bar">
                    <div className="phone-app-bar-title">
                        <span className="phone-app-bar-name" title={dialogTitle}>{dialogTitle}</span>
                        <div className="phone-battery">
                            <div className="phone-battery-body">
                                {hasBattery && (
                                    <div className={`phone-battery-fill ${batteryColorClass}`} style={{ width: `${batteryLevel}%` }} />
                                )}
                                <span className="phone-battery-pct">{hasBattery ? `${batteryLevel}%` : '--'}</span>
                            </div>
                            <div className="phone-battery-nub" />
                        </div>
                    </div>
                    <button className="phone-close-btn" onClick={handleClose} title="Close">&#10005;</button>
                </div>

                {/* Screen */}
                <div className="phone-screen">

                    {/* Error Banner */}
                    {temperatureErr && (
                        <div className="phone-error-banner">
                            <span className="phone-error-icon">&#9888;</span>
                            {temperatureErr}
                        </div>
                    )}

                    {/* Hero Temperature */}
                    <div className="phone-hero">
                        <div className={`phone-temp-card ${tempNumberColor}`}>
                            <span className="phone-temp-number">{temp.celsius}</span>
                            <span className="phone-temp-unit">°C</span>
                        </div>
                        <div className={`phone-temp-card ${tempNumberColor}`}>
                            <span className="phone-temp-number">{temp.fahrenheit}</span>
                            <span className="phone-temp-unit">°F</span>
                        </div>
                        <div className="phone-sync-row">
                            <span className="phone-sync-time">{temperatureData?.lastUpdatedTime || '--'}</span>
                            <button
                                className={`phone-refresh-btn ${getRetrieveFromZebraLoading ? 'spinning' : ''}`}
                                onClick={() => fetchSensorTemperatureData()}
                                disabled={getRetrieveFromZebraLoading}
                                title="Refresh"
                            >
                                &#8635;
                            </button>
                        </div>
                    </div>

                    {/* Recorded Temperature Log */}
                    <div className="phone-section">
                        <div className="phone-section-label">RECORDED TEMPERATURE</div>
                        <div className="phone-date-nav">
                            <button className="phone-nav-btn" onClick={goToPrevDay} disabled={getSensorsLogLoading}>
                                <span className="icon-arrow left">&#8249;</span>
                            </button>
                            <span className="phone-date-text">{sensorLogDate}</span>
                            <button className="phone-nav-btn" onClick={goToNextDay} disabled={isToday || getSensorsLogLoading}>
                                <span className="icon-arrow right">&#8250;</span>
                            </button>
                        </div>
                        <div className="phone-minmax-row">
                            <div className="phone-minmax-card min">
                                <div className="phone-minmax-lable-wrapper">
                                    <span className="phone-minmax-icon">&#8595;</span>
                                    <span className="phone-minmax-label">MIN</span>
                                </div>
                                <span className="phone-minmax-value">{tempSensorLog?.min || '--'}</span>
                                <span className="phone-minmax-unit">°C</span>
                            </div>
                            <div className="phone-minmax-divider"></div>
                            <div className="phone-minmax-card max">
                                <div className="phone-minmax-lable-wrapper">
                                    <span className="phone-minmax-icon">&#8593;</span>
                                    <span className="phone-minmax-label">MAX</span>
                                </div>
                                <span className="phone-minmax-value">{tempSensorLog?.max || '--'}</span>
                                <span className="phone-minmax-unit">°C</span>
                            </div>
                        </div>
                    </div>

                    {/* Status Cards */}
                    <div className="phone-section">
                        <div className="phone-section-label">STATUS</div>
                        <div className="phone-status-row">
                            <div className={`phone-status-card ${sensorStatusDot}`}>
                                <span className={`phone-status-dot ${sensorStatusDot}`}></span>
                                <span className="phone-status-card-title">Sensor</span>
                                <span className="phone-status-card-value">{temperatureData?.sensorStatus || '--'}</span>
                            </div>
                            <div className={`phone-status-card ${taskStatusDot}`}>
                                <span className={`phone-status-dot ${taskStatusDot}`}></span>
                                <span className="phone-status-card-title">Task</span>
                                <span className="phone-status-card-value">{temperatureData?.sensorTaskStatus || '--'}</span>
                            </div>
                        </div>
                    </div>

                    {/* <div className="phone-section footer">
                        <div className="phone-details-row">
                            <div className="phone-detail-item">{temperatureData?.serialNumber || "--"}</div>
                            <div className="phone-detail-item">Battery Level: {temperatureData?.batteryLevel || "--"}%</div>
                        </div>
                    </div> */}
                </div>
            </div>
        </Modal>
    )
}

export default TemperatureDisplayDialog;
